
import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, Filter, X } from 'lucide-react';
import { CANDIDATE_STATUS, EVALUATION_ROUNDS, QUALIFICATION_OPTIONS, BRANCH_OPTIONS, getQualificationTypeLabel, getBranchTypeLabel } from '@/types/enums';
import { getCandidateStatusLabel, getEvaluationRoundLabel } from '@/utils/formatters';
import { getCandidateStatusHint } from '@/utils/candidateStatus';
import { ScoreFilter, ScoreFilterCriteria } from './ScoreFilter';
import {
  ASSESSMENT_ROUND_FILTER_LABEL,
  ASSESSMENT_ROUND_FILTER_VALUE,
} from '@/constants/candidateRoundFilters';

interface Option {
  id: string;
  name: string;
}

interface JobPositionOption {
  id: string;
  role_name: string;
}

interface EnhancedCandidateFiltersProps {
  // Search
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  
  // Basic Filters
  statusFilter: string;
  setStatusFilter: (v: string) => void;
  roundFilter: string;
  setRoundFilter: (v: string) => void;
  collegeFilter: string;
  setCollegeFilter: (v: string) => void;
  positionFilter: string;
  setPositionFilter: (v: string) => void;
  cityFilter: string;
  setCityFilter: (v: string) => void;
  qualificationFilter: string;
  setQualificationFilter: (v: string) => void;
  branchFilter: string;
  setBranchFilter: (v: string) => void;
  emailSentFilter: string;
  setEmailSentFilter: (v: string) => void;
  yearFilter: string;
  setYearFilter: (v: string) => void;
  
  // Sort Options
  sortBy: string;
  setSortBy: (v: string) => void;
  sortOrder: 'asc' | 'desc';
  setSortOrder: (v: 'asc' | 'desc') => void;
  
  // Score Filters
  scoreFilters: ScoreFilterCriteria[];
  onAddScoreFilter: (filter: Omit<ScoreFilterCriteria, 'id'>) => void;
  onRemoveScoreFilter: (id: string) => void;
  onClearScoreFilters: () => void;
  
  // Control
  clearFilters: () => void;
  showFilters: boolean;
  setShowFilters: (v: boolean) => void;
  
  // Options
  colleges: Option[];
  jobPositions: JobPositionOption[];
  cities: string[];
}

const currentRoundFilterOptions = [
  EVALUATION_ROUNDS.CV_PROFILE,
  ASSESSMENT_ROUND_FILTER_VALUE,
  EVALUATION_ROUNDS.VOICE_INTERVIEW,
  EVALUATION_ROUNDS.CAMPUS_INTERVIEW,
  EVALUATION_ROUNDS.OFFICE_TECHNICAL,
  EVALUATION_ROUNDS.HR_FOUNDER,
];

const getCurrentRoundFilterLabel = (round: string) =>
  round === ASSESSMENT_ROUND_FILTER_VALUE
    ? ASSESSMENT_ROUND_FILTER_LABEL
    : getEvaluationRoundLabel(round as any);

const SEARCH_DEBOUNCE_MS = 300;

export function EnhancedCandidateFilters({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  roundFilter,
  setRoundFilter,
  collegeFilter,
  setCollegeFilter,
  positionFilter,
  setPositionFilter,
  cityFilter,
  setCityFilter,
  qualificationFilter,
  setQualificationFilter,
  branchFilter,
  setBranchFilter,
  emailSentFilter,
  setEmailSentFilter,
  yearFilter,
  setYearFilter,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  scoreFilters,
  onAddScoreFilter,
  onRemoveScoreFilter,
  onClearScoreFilters,
  clearFilters,
  showFilters,
  setShowFilters,
  colleges,
  jobPositions,
  cities,
}: EnhancedCandidateFiltersProps) {
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => (currentYear - i).toString());

  // Local input state for responsive typing — debounces writing to URL/query
  const [inputValue, setInputValue] = useState(searchTerm);
  const setSearchTermRef = useRef(setSearchTerm);
  useEffect(() => { setSearchTermRef.current = setSearchTerm; });

  // Sync from parent when search term changes externally (e.g. saved filter applied, clear all)
  useEffect(() => {
    setInputValue(searchTerm);
  }, [searchTerm]);

  // Debounce: only commit to URL after user stops typing
  useEffect(() => {
    if (inputValue === searchTerm) return;
    const timer = setTimeout(() => {
      setSearchTermRef.current(inputValue);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue, searchTerm]);

  const searchScopeLabel =
    yearFilter === 'all'
      ? 'Search covers candidates from all years.'
      : `Search covers candidates created in ${yearFilter} only.`;

  const hasActiveFilters =
    statusFilter !== 'all' ||
    roundFilter !== 'all' ||
    collegeFilter !== 'all' ||
    positionFilter !== 'all' ||
    cityFilter !== 'all' ||
    qualificationFilter !== 'all' ||
    branchFilter !== 'all' ||
    emailSentFilter !== 'all' ||
    yearFilter !== currentYear.toString() ||
    scoreFilters.length > 0;

  return (
    <div className="space-y-4">
      {/* Search and Filter Toggle */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[300px] space-y-1">
          <div className="flex items-center space-x-2">
            <Search className="h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name or email..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="flex-1"
            />
          </div>
          <p className="text-xs text-muted-foreground">{searchScopeLabel}</p>
        </div>
        
        <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
          <Filter className="h-4 w-4 mr-2" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
          {hasActiveFilters && (
            <span className="ml-2 bg-blue-500 text-white text-xs rounded-full px-2 py-0.5">
              {[statusFilter !== 'all', roundFilter !== 'all', collegeFilter !== 'all', positionFilter !== 'all', cityFilter !== 'all', qualificationFilter !== 'all', branchFilter !== 'all'].filter(Boolean).length + scoreFilters.length}
            </span>
          )}
        </Button>
        
        {hasActiveFilters && (
          <Button variant="outline" onClick={clearFilters}>
            <X className="h-4 w-4 mr-2" />
            Clear All Filters
          </Button>
        )}
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Filters */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Basic Filters</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Current Round</label>
                  <Select value={roundFilter} onValueChange={setRoundFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by round" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Rounds</SelectItem>
                      {currentRoundFilterOptions.map((round) => (
                        <SelectItem key={round} value={round}>
                          {getCurrentRoundFilterLabel(round)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Status</label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {Object.values(CANDIDATE_STATUS)
                        .filter(
                          (status) =>
                            status !== CANDIDATE_STATUS.ASSESSMENT_COMPLETED
                        )
                        .map((status) => (
                          <SelectItem key={status} value={status}>
                            {getCandidateStatusLabel(status as any)}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {getCandidateStatusHint(roundFilter === 'all' ? null : roundFilter)}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">College</label>
                  <Select value={collegeFilter} onValueChange={setCollegeFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by college" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Colleges</SelectItem>
                      {colleges.map((college) => (
                        <SelectItem key={college.id} value={college.id}>
                          {college.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Position</label>
                  <Select value={positionFilter} onValueChange={setPositionFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by position" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Positions</SelectItem>
                      {jobPositions.map((position) => (
                        <SelectItem key={position.id} value={position.id}>
                          {position.role_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">City</label>
                  <Select value={cityFilter} onValueChange={setCityFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by city" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Cities</SelectItem>
                      {cities.map((city) => (
                        <SelectItem key={city} value={city}>
                          {city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Qualification</label>
                  <Select value={qualificationFilter} onValueChange={setQualificationFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by qualification" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Qualifications</SelectItem>
                      {QUALIFICATION_OPTIONS.map((qualification) => (
                        <SelectItem key={qualification} value={qualification}>
                          {getQualificationTypeLabel(qualification)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Branch</label>
                  <Select value={branchFilter} onValueChange={setBranchFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by branch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Branches</SelectItem>
                      {BRANCH_OPTIONS.map((branch) => (
                        <SelectItem key={branch} value={branch}>
                          {getBranchTypeLabel(branch)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Email Sent</label>
                  <Select value={emailSentFilter} onValueChange={setEmailSentFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Email sent?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="not_sent">Not Sent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Year</label>
                  <Select value={yearFilter} onValueChange={setYearFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by year" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Years</SelectItem>
                      {yearOptions.map((year) => (
                        <SelectItem key={year} value={year}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sort Options */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sort Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Sort By</label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_at">Created Date</SelectItem>
                      <SelectItem value="full_name">Name</SelectItem>
                      <SelectItem value="status">Status</SelectItem>
                      <SelectItem value="final_score">Final Score</SelectItem>
                      <SelectItem value="cgpa">CGPA</SelectItem>
                      <SelectItem value="current_round">Current Round</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Sort Order</label>
                  <Select value={sortOrder} onValueChange={setSortOrder}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="desc">Descending</SelectItem>
                      <SelectItem value="asc">Ascending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Score Filters */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Score Filters</CardTitle>
            </CardHeader>
            <CardContent>
              <ScoreFilter
                scoreFilters={scoreFilters}
                onAddFilter={onAddScoreFilter}
                onRemoveFilter={onRemoveScoreFilter}
                onClearAll={onClearScoreFilters}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
